import { useMemo } from "react";

import { useResponsive } from "../../../../../hooks/useResponsive";
import { getNumberFormat, formatSignedCurrency } from "../../../../../utils";
import { BREAKPOINTS } from "../../../../../utils/constants";

import { Sparkline } from "../../../../../components/Sparkline";
import { SortableColumnHeader } from "../../../../../components/SortableColumnHeader";
import { buildSavingsHoldings, sortSavings } from "./holdings";
import {
  BodyRow,
  Card,
  CardGain,
  CardList,
  CardTop,
  CardTrendBox,
  CardValue,
  CardValueRow,
  CashbackNote,
  Cell,
  Dot,
  EarnedText,
  HeaderCell,
  HeaderRow,
  MobileSummary,
  MutedText,
  NameButton,
  NameText,
  Section,
  SinceFooter,
  SinceText,
  SummaryFooter,
  Table,
  TableCard,
  TableScroll,
  TrendBox,
  TrendCell,
  TypeLabel,
  TypeRow,
  ValueText,
} from "./styles";
import { SavingsHoldingsProps, SortKey } from "./types";

const NUMERIC_COLUMNS: { key: SortKey; label: string }[] = [
  { key: "balance", label: "Balance" },
  { key: "deposited", label: "Deposited" },
  { key: "withdrawn", label: "Withdrawn" },
  { key: "earned", label: "Earned" },
];

const cashbackNoteOf = (cashback: number): string | null =>
  cashback > 0 ? `+${getNumberFormat(cashback)} € cashback` : null;

const formatWithdrawn = (value: number): string => {
  if (value <= 0) {
    return "—";
  }
  return `${getNumberFormat(value)} €`;
};

export const SavingsHoldings: React.FC<SavingsHoldingsProps> = ({
  savings,
  bySaving,
  onSelect,
  sort,
  onSortChange,
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);

  const trendBySaving = useMemo(
    () =>
      new Map(
        bySaving.map((series) => [
          series.savingId,
          series.points.map((point) => point.value),
        ]),
      ),
    [bySaving],
  );

  const baseRows = useMemo(
    () => buildSavingsHoldings(savings, trendBySaving),
    [savings, trendBySaving],
  );
  const rows = useMemo(() => sortSavings(baseRows, sort), [baseRows, sort]);

  const totalBalance = useMemo(
    () => rows.reduce((sum, row) => sum + row.balance, 0),
    [rows],
  );

  const countLabel = `${rows.length} ${rows.length === 1 ? "saving" : "savings"}`;
  const summaryLabel = `${countLabel} · ${getNumberFormat(totalBalance)} € total`;

  const handleSort = (key: SortKey) => {
    if (sort.key === key) {
      onSortChange({ key, dir: sort.dir === "asc" ? "desc" : "asc" });
      return;
    }
    onSortChange({ key, dir: "desc" });
  };

  // The name button is the keyboard target; stop the click from also reaching
  // the row's mouse handler so a click on the name navigates only once.
  const handleNameClick = (event: React.MouseEvent, id: number) => {
    event.stopPropagation();
    onSelect(id);
  };

  const handleCardKeyDown = (event: React.KeyboardEvent, id: number) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(id);
    }
  };

  if (isCompact) {
    return (
      <Section>
        <CardList>
          {rows.map((row) => (
            <Card
              key={row.id}
              role="button"
              tabIndex={0}
              aria-label={`View ${row.name}`}
              onClick={() => onSelect(row.id)}
              onKeyDown={(event) => handleCardKeyDown(event, row.id)}
            >
              <CardTop>
                <NameText>{row.name}</NameText>
              </CardTop>
              <TypeRow>
                <Dot $color={row.accountColor} />
                <TypeLabel>{row.accountLabel}</TypeLabel>
              </TypeRow>
              <CardValueRow>
                <CardValue>{getNumberFormat(row.balance)} €</CardValue>
                <CardGain $tone={row.earnedTone}>
                  {formatSignedCurrency(row.earned)}
                  <CashbackNote>{cashbackNoteOf(row.cashback)}</CashbackNote>
                </CardGain>
              </CardValueRow>
              <CardTrendBox>
                <Sparkline data={row.trend} tone={row.earnedTone} />
              </CardTrendBox>
              <SinceFooter>Since {row.since}</SinceFooter>
            </Card>
          ))}
        </CardList>
        <MobileSummary>{summaryLabel}</MobileSummary>
      </Section>
    );
  }

  return (
    <Section>
      <TableCard>
        <TableScroll>
          <Table>
            <thead>
              <HeaderRow>
                <HeaderCell $align="left">Savings</HeaderCell>
                <SortableColumnHeader
                  column="since"
                  label="Since"
                  align="start"
                  sort={sort}
                  onSort={handleSort}
                />
                {NUMERIC_COLUMNS.map((column) => (
                  <SortableColumnHeader
                    key={column.key}
                    column={column.key}
                    label={column.label}
                    align="end"
                    sort={sort}
                    onSort={handleSort}
                  />
                ))}
                <HeaderCell $align="right">Trend</HeaderCell>
              </HeaderRow>
            </thead>
            <tbody>
              {rows.map((row) => (
                <BodyRow key={row.id} onClick={() => onSelect(row.id)}>
                  <Cell $align="left">
                    <NameButton
                      type="button"
                      onClick={(event) => handleNameClick(event, row.id)}
                    >
                      {row.name}
                    </NameButton>
                    <TypeRow>
                      <Dot $color={row.accountColor} />
                      <TypeLabel>{row.accountLabel}</TypeLabel>
                    </TypeRow>
                  </Cell>
                  <Cell $align="left">
                    <SinceText>{row.since}</SinceText>
                  </Cell>
                  <Cell $align="right">
                    <ValueText>{getNumberFormat(row.balance)} €</ValueText>
                  </Cell>
                  <Cell $align="right">
                    <MutedText>{getNumberFormat(row.deposited)} €</MutedText>
                  </Cell>
                  <Cell $align="right">
                    <MutedText>{formatWithdrawn(row.withdrawn)}</MutedText>
                  </Cell>
                  <Cell $align="right">
                    <EarnedText $tone={row.earnedTone}>
                      {formatSignedCurrency(row.earned)}
                    </EarnedText>
                    <CashbackNote>{cashbackNoteOf(row.cashback)}</CashbackNote>
                  </Cell>
                  <TrendCell>
                    <TrendBox>
                      <Sparkline data={row.trend} tone={row.earnedTone} />
                    </TrendBox>
                  </TrendCell>
                </BodyRow>
              ))}
            </tbody>
          </Table>
        </TableScroll>
        <SummaryFooter>{summaryLabel}</SummaryFooter>
      </TableCard>
    </Section>
  );
};
